// src/components/analytics/AnalyticsContents.jsx
import React, { useEffect, useState } from 'react';
import './Analytics.css';
import './plots/plots.css';

// API and transformers
import { analyticsAPI } from '../../services/api';
import { 
  transformMonthlyTotalInsects,
  transformMonthlyCaptureTrend,
  transformSpeciesIdentifiedByLocation,
  transformSpeciesTrendByLocation
} from '../../utils/dataTransformers';
// Plot components
import { TtcByClientChart, TtcByClientTable } from './plots/TotalInsectsTrapped.jsx';
import { CaptureTrendByAreaChart, CaptureTrendByAreaTable } from './plots/CaptureTrendAnalysis.jsx';
import { SpeciesIdentifiedByLocationChart, SpeciesIdentifiedByLocationTable } from './plots/SpeciesIdentifiedAnalysis.jsx';
import { SpeciesTrendByLocationChart, SpeciesTrendByLocationTable } from './plots/SpeciesTrendAnalysis.jsx';


export default function AnalyticsContents({ filters = {}, result = null, onDataLoaded }) {
  const [dataPayload, setDataPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { type } = filters || {};

  // Fetch data when result changes (after Generate is clicked)
  useEffect(() => {
    if (!result || !type) {
      setDataPayload(null);
      setError(null);
      if (onDataLoaded) onDataLoaded(null);
      return;
    }

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        
        let payload;
        
        switch (type) {
          case 'Monthly Total Insects Trapped': {
            const response = await analyticsAPI.getMonthlyTotalInsects(filters);
            payload = transformMonthlyTotalInsects(response.data);
            break;
          }
          
          case 'Monthly Capture Trend Analysis': {
            const response = await analyticsAPI.getMonthlyCaptureTrend(filters);
            payload = transformMonthlyCaptureTrend(response.data);
            break;
          }
          
          case 'Species Identified Analysis of The Month': {
            const response = await analyticsAPI.getSpeciesIdentifiedByLocation(filters);
            payload = transformSpeciesIdentifiedByLocation(response.data);
            break;
          }
          
          case 'Monthly Species Trend Analysis': {
            const response = await analyticsAPI.getSpeciesTrendByLocation(filters);
            payload = transformSpeciesTrendByLocation(response.data);
            break;
          }
          
          default:
            payload = null;
        }
        
        setDataPayload(payload);
        if (onDataLoaded) onDataLoaded(payload);
      } catch (err) {
        console.error('Failed to fetch analytics data:', err);
        setError(err.message || 'Failed to load data');
        setDataPayload(null);
        if (onDataLoaded) onDataLoaded(null);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [result, type, filters, onDataLoaded]);

  // ... rest of the component stays the same
  
  // Empty state
  if (!type) {
    return (
      <section className="acnt">
        <div className="acnt__msg">
          Select a <strong>Type of Analysis</strong>
        </div>
      </section>
    );
  }

  // Ready state
  if (type && !result) {
    return (
      <section className="acnt">
        <div className="acnt__msg">
          Select all settings and Click <strong>GENERATE</strong> to render chart/table
        </div>
      </section>
    );
  }

  // Loading state
  if (loading) {
    return (
      <section className="acnt">
        <div className="acnt__msg">Loading data...</div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="acnt">
        <div className="acnt__msg" style={{ color: '#dc2626' }}>
          Error: {error}
        </div>
      </section>
    );
  }

  // No data state
  if (!dataPayload) {
    return (
      <section className="acnt">
        <div className="acnt__msg">No data available for selected filters</div>
      </section>
    );
  }

  // Choose renderers
  let ChartComp = null;
  let TableComp = null;

  switch (type) {
    case 'Monthly Total Insects Trapped':
      ChartComp = TtcByClientChart;
      TableComp = TtcByClientTable;
      break;

    case 'Monthly Capture Trend Analysis':
      ChartComp = CaptureTrendByAreaChart;
      TableComp = CaptureTrendByAreaTable;
      break;

    case 'Species Identified Analysis of The Month':
      ChartComp = SpeciesIdentifiedByLocationChart;
      TableComp = SpeciesIdentifiedByLocationTable;
      break;

    case 'Monthly Species Trend Analysis':
      ChartComp = SpeciesTrendByLocationChart;
      TableComp = SpeciesTrendByLocationTable;
      break;

    default:
      return (
        <section className="acnt">
          <div className="acnt__msg">(This analysis type is not implemented yet)</div>
        </section>
      );
  }

  const isFullChartOnly = (type === 'Monthly Species Trend Analysis');

  return (
    <section className="acnt">
      {result?.generatedAt && (
        <div className="acnt__generated">
          Generated at {new Date(result.generatedAt).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })}
        </div>
      )}

      <div className="acnt__sec2">
        {isFullChartOnly ? (
          <section className="acnt__panel acnt__panel--chart acnt__panel--full">
            <div className="acnt__panel-body plot-lines">
              <ChartComp data={dataPayload} />
            </div>
          </section>
        ) : (
          <>
            <section className="acnt__panel acnt__panel--chart">
              <div className="acnt__panel-body">
                <ChartComp data={dataPayload} />
              </div>
            </section>

            <section className="acnt__panel acnt__panel--table">
              <div className="acnt__panel-body acnt__panel-scroll">
                <TableComp data={dataPayload} />
              </div>
            </section>
          </>
        )}
      </div>
    </section>
  );
}